import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import {
  Alert,
  Badge,
  FormGroup,
  Input,
  Spinner,
} from "reactstrap";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { getPublicUrl } from "../../../utils/Utils";
import { CASE_STATUSES, formatMoney } from "./soHelpers";
import { meCan } from "../../../utils/adminPermissions";

function patientName(p) {
  if (!p || typeof p !== "object") return "—";
  return [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || p.email || "—";
}

function caseRef(id) {
  const tail = String(id || "")
    .replace(/[^a-f0-9]/gi, "")
    .slice(-4)
    .toUpperCase();
  return tail ? `SO-${tail}` : "SO-—";
}

function initials(name) {
  const parts = String(name || "")
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || "?").slice(0, 2).toUpperCase();
}

function fmtDay(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function auditLabel(entry) {
  const a = entry?.action || "";
  const map = {
    assign_expert: "Assigned specialist",
    status_change: "Status changed",
    patient_remind: "Patient reminded",
    refund: "Refund issued",
  };
  return map[a] || a.replace(/_/g, " ");
}

const SoCaseDetail = () => {
  const { caseId } = useParams();
  const [row, setRow] = useState(null);
  const [docs, setDocs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("");
  const [msgText, setMsgText] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(
    async (signal) => {
      if (!caseId) return;
      setLoading(true);
      setError("");
      try {
        const [cRes, aRes, mRes] = await Promise.all([
          http.get(`/api/v1/admin/second-opinion/cases/${caseId}`, {
            signal,
            suppressInlineErrorSwal: true,
          }),
          http.get(`/api/v1/admin/second-opinion/cases/${caseId}/audit`, {
            signal,
            suppressInlineErrorSwal: true,
          }),
          http.get(`/api/v1/second-opinion/cases/${caseId}/messages`, {
            signal,
            suppressInlineErrorSwal: true,
          }),
        ]);
        const payload = cRes.data?.data;
        const caseRow = payload?.case || payload;
        setRow(caseRow || null);
        setDocs(Array.isArray(payload?.documents) ? payload.documents : []);
        setStatus(caseRow?.status || "");
        setAudit(Array.isArray(aRes.data?.data) ? aRes.data.data : []);
        setMessages(Array.isArray(mRes.data?.data) ? mRes.data.data : []);
      } catch (err) {
        if (isAbortError(err)) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    },
    [caseId]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const act = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Action failed");
    } finally {
      setBusy("");
    }
  };

  const reviewStarted = Boolean(row?.reviewStartedAt);
  const refunded = Boolean(row?.refundedAt);
  const question =
    row?.form?.questions || row?.form?.chiefComplaint || "—";
  const feeSnap = row?.expertFeeSnapshot;
  const due =
    row?.offer?.slaDays != null && (row.paidAt || row.createdAt)
      ? (() => {
          const d = new Date(row.paidAt || row.createdAt);
          d.setDate(d.getDate() + Number(row.offer.slaDays));
          return d;
        })()
      : null;

  return (
    <>
      <Head title={`Case ${caseRef(caseId)}`} />
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <div className="small text-soft mb-1">
              <Link to={getPublicUrl("second-opinion/cases")}>Second opinion</Link>
              {" > "}
              <Link to={getPublicUrl("second-opinion/cases")}>Cases</Link>
              {" > "}
              {caseRef(caseId)}
            </div>
            <BlockTitle page tag="h3">
              {(row?.specialty?.label || "Medical") + " second opinion"}{" "}
              {row?.status ? (
                <Badge color="primary" className="ms-2" style={{ fontSize: 12, fontWeight: 500 }}>
                  {String(row.status).replace(/_/g, " ")}
                </Badge>
              ) : null}
            </BlockTitle>
            {row ? (
              <p className="text-soft mb-0 small">
                {patientName(row.patient)}
                {" · "}
                {row.includesVisio ? "Written opinion + video" : "Written opinion"}
                {" · "}
                {row.paidAt
                  ? `Paid ${row.amount != null ? `${row.amount} ${String(row.currency || "").toUpperCase()}` : ""}`
                  : "Unpaid"}
                {due ? (
                  <>
                    {" · "}
                    <span style={{ color: "#92400e", fontWeight: 600 }}>
                      Due {fmtDay(due)}
                    </span>
                  </>
                ) : null}
              </p>
            ) : null}
          </BlockHeadContent>
        </BlockHead>

        {error ? (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <div className="text-center py-5">
            <Spinner size="sm" />
          </div>
        ) : !row ? (
          <Alert color="warning">Case not found.</Alert>
        ) : (
          <Block>
            <div className="row g-3">
              <div className="col-lg-8">
                <div className="card card-bordered mb-3">
                  <div className="card-inner">
                    <h6 className="title">Patient question</h6>
                    <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                      {question}
                    </p>
                  </div>
                </div>

                <div className="card card-bordered mb-3">
                  <div className="card-inner">
                    <h6 className="title">
                      Documents · {docs.length} uploaded
                    </h6>
                    {docs.length === 0 ? (
                      <p className="text-soft mb-0 small">No documents yet.</p>
                    ) : (
                      docs.map((d) => (
                        <div
                          key={String(d._id)}
                          className="d-flex align-items-center justify-content-between py-2 border-bottom"
                        >
                          <span className="d-flex align-items-center gap-2">
                            <Icon name="check-circle" className="text-success" />
                            {d.originalName || d.filename || "Document"}
                          </span>
                          <a
                            href={`/api/v1/second-opinion/cases/${caseId}/documents/${d._id}/download`}
                            className="btn btn-icon btn-sm btn-outline-light"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Icon name="download" />
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="card card-bordered mb-3">
                  <div className="card-inner">
                    <h6 className="title">Messages</h6>
                    <div className="mb-3" style={{ maxHeight: 240, overflowY: "auto" }}>
                      {messages.length === 0 ? (
                        <p className="text-soft small mb-0">No messages yet.</p>
                      ) : (
                        messages.map((m) => (
                          <div key={String(m._id)} className="mb-2 small">
                            <strong>
                              {m.role === "patient" ? "Patient" : "Care team"}
                            </strong>
                            <span className="text-soft"> · {fmtDay(m.createdAt)}</span>
                            <div>{m.text}</div>
                          </div>
                        ))
                      )}
                    </div>
                    <FormGroup className="mb-2">
                      <Input
                        type="textarea"
                        rows={2}
                        value={msgText}
                        onChange={(e) => setMsgText(e.target.value)}
                        placeholder="Message to patient…"
                      />
                    </FormGroup>
                    <Button
                      color="primary"
                      size="sm"
                      disabled={!!busy || !msgText.trim()}
                      onClick={() =>
                        act("msg", () =>
                          http.post(
                            `/api/v1/second-opinion/cases/${caseId}/messages`,
                            { text: msgText.trim() },
                            { suppressInlineErrorSwal: true }
                          ).then(() => setMsgText(""))
                        )
                      }
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card card-bordered mb-3">
                  <div className="card-inner">
                    <h6 className="title">Specialist</h6>
                    {row.expert ? (
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span
                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{
                            width: 36,
                            height: 36,
                            background: "#dbeafe",
                            color: "#1d4ed8",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {initials(row.expert.displayName)}
                        </span>
                        <div>
                          <div className="fw-medium">{row.expert.displayName}</div>
                          {feeSnap?.amount != null ? (
                            <div className="small text-soft">
                              Frozen {formatMoney(feeSnap.amount, feeSnap.currency)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <p className="text-soft small">Not assigned yet.</p>
                    )}
                    <Link
                      to={getPublicUrl(`second-opinion/experts?assign=${caseId}`)}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <Icon name="user-add" className="me-1" />
                      {row.expert ? "Reassign" : "Assign"}
                    </Link>
                  </div>
                </div>

                <div className="card card-bordered mb-3">
                  <div className="card-inner">
                    <h6 className="title">Status</h6>
                    <FormGroup>
                      <Input
                        type="select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        {CASE_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, " ")}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        disabled={!!busy || status === row.status}
                        onClick={() =>
                          act("status", () =>
                            http.patch(
                              `/api/v1/admin/second-opinion/cases/${caseId}/status`,
                              { status },
                              { suppressInlineErrorSwal: true }
                            )
                          )
                        }
                      >
                        Update
                      </Button>
                      <Button
                        color="warning"
                        size="sm"
                        outline
                        disabled={!!busy}
                        onClick={() =>
                          act("remind", () =>
                            http.post(
                              `/api/v1/admin/second-opinion/cases/${caseId}/remind`,
                              {
                                reason:
                                  "Please upload the missing documents for your second-opinion dossier.",
                              },
                              { suppressInlineErrorSwal: true }
                            )
                          )
                        }
                      >
                        <Icon name="mail" className="me-1" />
                        Remind
                      </Button>
                    </div>
                  </div>
                </div>

                <div
                  className="card card-bordered mb-3"
                  style={
                    reviewStarted && !refunded
                      ? { background: "#fef9c3", borderColor: "#fde68a" }
                      : undefined
                  }
                >
                  <div className="card-inner">
                    <h6 className="title">Refund</h6>
                    {refunded ? (
                      <p className="small mb-0">
                        Refunded {row.refundAmount} {String(row.currency || "").toUpperCase()}
                      </p>
                    ) : !meCan("finance.refund.act") ? (
                      <p className="small text-soft mb-0">
                        You do not have permission to issue refunds.
                      </p>
                    ) : reviewStarted ? (
                      <>
                        <p className="small">
                          Analysis started — automatic refund not applicable.
                        </p>
                        <Button
                          color="light"
                          size="sm"
                          disabled={!!busy}
                          onClick={() => {
                            const amount = window.prompt(
                              "Manual refund amount (major units, e.g. 229):"
                            );
                            if (!amount) return;
                            act("refund", () =>
                              http.post(
                                `/api/v1/admin/second-opinion/cases/${caseId}/refund`,
                                { force: true, amount: Number(amount) },
                                { suppressInlineErrorSwal: true }
                              )
                            );
                          }}
                        >
                          Refund manually
                        </Button>
                      </>
                    ) : (
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        disabled={!!busy}
                        onClick={() =>
                          act("refund", () =>
                            http.post(
                              `/api/v1/admin/second-opinion/cases/${caseId}/refund`,
                              {},
                              { suppressInlineErrorSwal: true }
                            )
                          )
                        }
                      >
                        Full refund
                      </Button>
                    )}
                  </div>
                </div>

                <div className="card card-bordered">
                  <div className="card-inner">
                    <h6 className="title">
                      <Icon name="list-check" className="me-1" />
                      Journal
                    </h6>
                    {audit.length === 0 ? (
                      <p className="text-soft small mb-0">No events yet.</p>
                    ) : (
                      <ul className="list-unstyled mb-0 small">
                        {audit.map((e) => (
                          <li key={String(e._id)} className="mb-2">
                            <span className="text-soft">{fmtDay(e.performedAt)} · </span>
                            {auditLabel(e)}
                            {e.details?.to ? ` → ${e.details.to}` : ""}
                            {e.details?.expertId && e.action === "assign_expert"
                              ? ` (${String(e.details.expertId).slice(-6)})`
                              : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Block>
        )}
      </Content>
    </>
  );
};

export default SoCaseDetail;
